'use client'

import { useTranslations } from 'next-intl'

import type { MenuItem, MenuSeparator } from '@graftcode/design-system'
import { useShallow } from 'zustand/react/shallow'

import { LAYOUT_TEST_IDS } from '@common/common-test-ids.const'
import { DEFAULT_PROFILE_ITEMS } from '@common/const'
import { AppVariant } from '@common/enums'
import { isDevEnvironment } from '@common/helpers'

import { selectProjectsShellState } from '@modules/projects/stores/projects.selector'
import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { selectWorkspacesShellState } from '@modules/workspaces/stores/workspaces.selector'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'
import { selectGlobalLoading } from '@stores/layout/layout.selector'
import { useLayoutStore } from '@stores/layout/layout.store'
import { selectUnreadNotificationCount } from '@stores/notifications/notifications.selector'
import { useNotificationsStore } from '@stores/notifications/notifications.store'

import { Header } from '../../Header'
import { useLayoutNavigation } from '../hooks/useLayoutNavigation'

type LayoutTopbarHostProps = {
  helpItems: (MenuItem | MenuSeparator)[]
  onNotificationClick?: () => void
  searchSlot?: React.ReactNode
  selectedNavId: AppVariant
}

export function LayoutTopbarHost({ helpItems, onNotificationClick, searchSlot, selectedNavId }: LayoutTopbarHostProps) {
  const t = useTranslations('core')
  const isPortal = selectedNavId === AppVariant.PORTAL
  const layoutLoading = useLayoutStore(selectGlobalLoading)
  const feedbackPopoverOpen = useLayoutStore((state) => state.feedbackPopoverOpen)
  const setFeedbackPopoverOpen = useLayoutStore((state) => state.setFeedbackPopoverOpen)
  const toggleMobileMenu = useLayoutStore((state) => state.toggleMobileMenu)
  const { currentWorkspace, loading: workspacesLoading } = useWorkspacesStore(useShallow(selectWorkspacesShellState))
  const { projects, currentProject, loading: projectsLoading } = useProjectsStore(useShallow(selectProjectsShellState))
  const notificationsEnabled = isDevEnvironment()
  const unreadCount = useNotificationsStore(selectUnreadNotificationCount)
  const notificationBadge = notificationsEnabled ? unreadCount : undefined

  const projectSelectorLoading =
    layoutLoading ||
    workspacesLoading ||
    projectsLoading ||
    (!!currentWorkspace && projects.length > 0 && !currentProject)

  const { handleProfileClick, handleHelpClick, handleProjectSelect } = useLayoutNavigation({ isPortal })

  return (
    <div data-testid={LAYOUT_TEST_IDS.layoutTopbarHost} style={{ display: 'contents' }}>
      <Header
        profileItems={DEFAULT_PROFILE_ITEMS}
        helpItems={helpItems}
        searchDisabled={!searchSlot}
        notificationBadge={notificationBadge}
        notificationsDisabled={!notificationsEnabled}
        projectSelectorLoading={projectSelectorLoading}
        projectSelectorDisabled={projectSelectorLoading}
        comingSoonLabel={searchSlot ? undefined : t('sidebar.comingSoon')}
        feedbackPopoverOpen={feedbackPopoverOpen}
        onFeedbackPopoverOpenChange={setFeedbackPopoverOpen}
        onProfileClick={handleProfileClick}
        onHelpClick={handleHelpClick}
        onMenuToggle={toggleMobileMenu}
        onNotificationClick={notificationsEnabled ? onNotificationClick : undefined}
        onProjectSelect={handleProjectSelect}
        searchSlot={searchSlot}
      />
    </div>
  )
}
